 <template>
  <div>
    <v-alert dark color="danger" border="top" v-if="startTemperaturesInCustomerFormat.length === 0">
      {{ this.$t('reports_ui.start_temperatures_report.no_results') }}
    </v-alert>
    <div v-else>
      <c3-chart
        ref="start-temperatures-histogram"
        :height="300"
        :data="startTemperatureChartData"
        :config="startTemperatureChartConfig"
        type="bar"/>
      <div v-show="showNormalDist" class="text-subtitle-2 py-3 text-center font-italic">
        {{ $t('reports_ui.start_temperatures_report.start_temperatures_follow_normal_distribution_blurb') }}
      </div>
    </div>
  </div>
</template>

<script>

import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ReportRecord from '@/orm/models/ReportRecord'
import TemperatureConverterUtil from '@/util/TemperatureConverterUtil'
import C3Chart from '@/components/shared/chart/C3Chart.vue'
import ArrayStats from '@/util/ArrayStats'
import * as d3 from 'd3'
import NormalDistribution from 'normal-distribution'
import { get, filter, keyBy, round, forEach } from 'lodash'

export default {
  name: 'StartTemperaturesReport',
  components: { C3Chart },
  mixins: [CurrentUserMixin],
  props: {
    report: {
      type: ReportRecord
    }
  },
  data () {
    return {
      expandedStartTemperatures: [],
      startTemperaturesInCustomerFormat: [],
      xAxisCategories: [],
      mean: null,
      standardDeviation: null,
      variance: null,
      normDist: null,
      normalDistInRange: [],
      temperatureCountsInRange: []
    }
  },
  methods: {
    init () {
      this.expandedStartTemperatures = []
      this.startTemperaturesInCustomerFormat = []
      this.xAxisCategories = []
      this.mean = null
      this.standardDeviation = null
      this.variance = null
      this.normDist = null
      this.normalDistInRange = []
      this.temperatureCountsInRange = []

      // Get and convert all temperatures into the desired customer format (celsius or fahrenheit)
      const startTemperatures = Object.values(get(this, 'report.results') || {})
      const customerTemperatureFormat = this.customerContext.tempFormat()

      forEach(startTemperatures, (row) => {
        this.startTemperaturesInCustomerFormat.push({
          count: row.count,
          startTemperature: round(TemperatureConverterUtil.to(row.startTemperature, 'C', customerTemperatureFormat), 1)
        })
      })

      // Start temperatures come to us as objects containing the temp and the number of instances of that temp (e.g.
      // 5 deg, 10 times). This loop constructs an array of temperatures with an entry for each time the temperature is
      // counted (e.g. from the example above 5 would show up 10 times in this array). We use this array to generate the
      // average, standard deviation, and variance values.
      forEach(this.startTemperaturesInCustomerFormat, (row) => {
        for (let i = 0; i < row.count; i++) {
          this.expandedStartTemperatures.push(row.startTemperature)
        }
      })

      this.mean = ArrayStats.mean(this.expandedStartTemperatures)
      this.standardDeviation = ArrayStats.standardDeviation(this.expandedStartTemperatures)
      this.variance = ArrayStats.variance(this.expandedStartTemperatures)
      this.normDist = new NormalDistribution(this.mean, this.standardDeviation)

      const curveLow = this.mean - (3 * this.standardDeviation)
      const curveHigh = this.mean + (3 * this.standardDeviation)
      const plotLow = round(Math.min(curveLow, Math.min(...this.expandedStartTemperatures)), 1)
      const plotHigh = round(Math.max(curveHigh, Math.max(...this.expandedStartTemperatures)), 1)
      const startTemperaturesByTemp = keyBy(this.startTemperaturesInCustomerFormat, 'startTemperature')

      // In order to align the normal distribution curve and the histogram we want the arrays to contain the same number
      // of elements. To do this we start at the beginning on the x-axis plot and for each point in the range (moving at
      // .1 deg increments) we calculate the probability density, adding it to the normalDistInRange array, and we see
      // if there is a value for temperature counts at that interval, adding it to the temperatureCountsInRange if so.
      let plotPoint = plotLow
      let previousPlotPoint

      while (plotPoint <= plotHigh) {
        this.xAxisCategories.push(plotPoint)
        if (plotPoint >= curveLow && plotPoint <= curveHigh) {
          // So this is an odd one. The x-axis does not move in regular increments in fahrenheit (because the original
          // data we have is in celsius and .1 degree increments in celsius may be .1 or .2 degree movements in
          // fahrenheit when rounded). Because of this irregular increment we get a "wavy" distribution curve when we
          // plot points that are not at the same interval. To reduce this effect by only plotting values when the plot
          // point has moved a consistent increment (.2 degrees) when in fahrenheit.
          if (customerTemperatureFormat.toLowerCase() === 'c' || (previousPlotPoint === undefined || previousPlotPoint < curveLow || round(Math.abs(previousPlotPoint - plotPoint), 1) === 0.2)) {
            this.normalDistInRange.push(this.normDist.pdf(plotPoint))
          } else {
            this.normalDistInRange.push(null)
          }
        } else {
          this.normalDistInRange.push(null)
        }
        this.temperatureCountsInRange.push(get(startTemperaturesByTemp[plotPoint], 'count', 0))
        previousPlotPoint = plotPoint
        plotPoint = this.incrementInCelsius(plotPoint, customerTemperatureFormat, 0.1)

        // round() is needed to address floating point errors
        plotPoint = round(plotPoint, 1)
      }
    },

    /**
     * Given an initial value and a format/scale this function will increment the value by the specified increment in
     * degrees celsius. This is only a little complicated because when in fahrenheit the size of this increment
     * changes the based on how far away from 0 c we are.
     *
     * @param {Number} temperature
     * @param {String} format indicates the temperature scale (f or c)
     * @param {Number} increment amount by which to increment the value
     * @return Number
     */
    incrementInCelsius (temperature, format, increment) {
      // Convert the number to celsuis if necessary
      if (format.toLowerCase() === 'f') {
        temperature = round(TemperatureConverterUtil.to(temperature, 'f', 'c'), 1)
      }

      // perform the increment
      temperature += increment

      // Convert it back again only if necessary
      if (format.toLowerCase() === 'f') {
        temperature = round(TemperatureConverterUtil.to(temperature, 'c', 'f'), 1)
      }

      return temperature
    },

    /**
     * If the customer context changes then regenerate the chart in order to update axis lables, etc... (called by the
     * CurrentUserMixin)
     * @return undefined
     */
    customerContextChangeHandler () {
      this.init()
      this.$refs['start-temperatures-histogram'].regenerate()
    }

  },
  computed: {
    /**
     * Returns a boolean value indicating if the current user should be shown the normal distribution curve.
     * @return boolean
     */
    showNormalDist () {
      return this.user.can('view_rootentity')
    },

    /**
     * Returns the histogram data formatted according to what C3 Chart needs to render as a bar graph.
     * @return object
     */
    startTemperatureChartData () {
      const chartData = {
        columns: [
          ['count'].concat(this.temperatureCountsInRange)
        ],
        names: {
          count: 'Route Count'
        },
        axes: {
          count: 'y'
        }
      }

      if (this.showNormalDist) {
        chartData.columns.push(['normalDist'].concat(this.normalDistInRange))
        chartData.names.normalDist = 'Normal Distribution*'
        chartData.axes.normalDist = 'y2'
        chartData.types = { normalDist: 'spline' }
      }

      return chartData
    },

    /**
     * Returns the legend for the histogram
     * @return object
     */
    startTemperatureChartConfig () {
      const formattedXAxisCategories = []

      const temperatureFormat = this.customerContext.tempFormat()
      this.xAxisCategories.forEach(temperature => {
        formattedXAxisCategories.push(`${TemperatureConverterUtil.formatTemperature(temperature)} °${temperatureFormat}`)
      })

      return {
        oninit: function () {
          const bar = d3.select('.chart-root > svg > g')
          bar.append('text')
            .attr('x', 10)
            .attr('y', 10)
            .attr('dy', '.35em')
            .text(`StdDev: ${TemperatureConverterUtil.formatTemperature(this.standardDeviation)} °${temperatureFormat}`)

          bar.append('text')
            .attr('x', 10)
            .attr('y', 25)
            .attr('dy', '.35em')
            .text(`Mean: ${TemperatureConverterUtil.formatTemperature(this.mean)} °${temperatureFormat}`)

          bar.append('text')
            .attr('x', 10)
            .attr('y', 40)
            .attr('dy', '.35em')
            .text(`Variance: ${TemperatureConverterUtil.formatTemperature(this.variance)} °${temperatureFormat}`)
        }.bind(this),
        axis: {
          x: {
            type: 'category',
            categories: formattedXAxisCategories,
            tick: {
              rotate: -70,
              multiline: false
            },
            height: 45
          }
        },
        point: {
          show: false
        },
        line: {
          connectNull: true
        },
        tooltip: {
          /**
           * Filter out the normal distribution from the tooltip (it'll only lead to confusion)
           *
           * @param array d array of data in the chart
           * @param function defaultTitleFormat
           * @param function defaultValueFormat
           * @param function color
           *
           * @return string
           */
          contents: function (d, defaultTitleFormat, defaultValueFormat, color) {
            // Filter out the normal dist data from the set of data to show in the tool tip.
            const data = filter(d, (data) => {
              return data.id !== 'normalDist'
            })
            // After stripping out the normal dist data, use the standard getTooltipContent() method to render the
            // tooltip markup.
            return this.getTooltipContent(data, defaultTitleFormat, defaultValueFormat, color)
          }
        }
      }
    }
  },
  mounted () {
    this.init()
  },
  watch: {
    report (newValue, oldValue) {
      this.init()
    }
  }
}

</script>
